import { makeStyles, createStyles } from "@material-ui/core";

/**
 * CMP Accelerator Member Dashboard design (CMP Accelerator.dc.html,
 * data-screen-label="Create Company Perks") for the member perk form.
 * Used only when the signed-in member has CMP access (cmpEnabled); CPA-only
 * members and chamber staff keep perkFormStyles.ts.
 */
export const CMP = {
    gold500: "#E1A731",
    gold600: "#C6912A",
    gold100: "#FBF0DC",
    black900: "#0A0A0A",
    text: "#000000",
    gray600: "#727272",
    gray400: "#9E9E9E",
    pageBg: "#F7F7F8",
    white: "#FFFFFF",
    green500: "#10B981",
    red: "#ED1C24",
    font: "'DM Sans', sans-serif",
};

const mobile = "@media (max-width: 600px)";

export const useCmpPerkFormStyles = makeStyles(() =>
    createStyles({
        root: {
            minHeight: "100vh",
            background: CMP.pageBg,
            paddingBottom: "48px",
            fontFamily: CMP.font,
            color: CMP.text,
            overflowX: "hidden",
            "& *": { boxSizing: "border-box" as const },
            "& input, & textarea, & button": { fontFamily: CMP.font },
        },
        header: {
            background: CMP.gold500,
            padding: "28px 40px 100px",
            borderRadius: "0 0 18px 18px",
            [mobile]: { padding: "20px 16px 92px" },
        },
        title: {
            fontSize: "28px",
            fontWeight: 600,
            color: CMP.white,
            margin: "0 0 8px",
            lineHeight: 1.25,
            [mobile]: { fontSize: "22px" },
        },
        subtitle: {
            fontSize: "15px",
            color: CMP.white,
            marginBottom: "18px",
        },
        card: {
            background: CMP.white,
            borderRadius: "18px",
            margin: "-72px 28px 0",
            padding: "36px 40px 40px",
            display: "flex",
            flexDirection: "column",
            gap: "36px",
            [mobile]: { margin: "-72px 12px 0", padding: "24px 16px 28px" },
        },
        section: { display: "flex", flexDirection: "column", gap: "10px" },
        sectionWide: { display: "flex", flexDirection: "column", gap: "12px" },
        label: { fontSize: "14px", fontWeight: 600, color: CMP.text },
        asterisk: { color: CMP.red },
        helper: { fontSize: "14px", color: CMP.text, lineHeight: 1.6, margin: 0 },
        helperPlain: { fontSize: "14px", color: CMP.text, margin: 0 },
        fieldBox: {
            borderRadius: "8px",
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            background: CMP.white,
        },
        textarea: {
            fontSize: "14px",
            lineHeight: 1.6,
            border: "none",
            outline: "none",
            resize: "vertical" as const,
            color: CMP.text,
            width: "100%",
            padding: "2px",
            background: "transparent",
        },
        textInput: {
            fontSize: "14px",
            border: "none",
            outline: "none",
            color: CMP.text,
            width: "100%",
            padding: "1px 2px",
            background: "transparent",
        },
        fieldError: { fontSize: "13px", fontWeight: 600, color: CMP.red },
        counterRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap" as const,
        },
        counter: { fontSize: "12px", color: CMP.text },
        idaGroup: { display: "inline-flex", alignItems: "center", gap: "10px" },
        idaMascot: { height: "30px", width: "auto", flexShrink: 0 },
        generateButton: {
            background: CMP.green500,
            color: CMP.white,
            border: "none",
            borderRadius: "999px",
            padding: "6px 18px",
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
        },
        comingSoon: {
            background: "#E53935",
            color: CMP.white,
            borderRadius: "999px",
            padding: "6px 18px",
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap" as const,
        },
        radioList: { display: "flex", flexDirection: "column", gap: "14px", marginTop: "4px" },
        radioGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            marginTop: "4px",
            [mobile]: { gridTemplateColumns: "1fr" },
        },
        radioLabel: {
            margin: 0,
            alignItems: "flex-start",
            gap: "12px",
            "& .MuiRadio-root": { padding: 0, marginTop: "1px" },
            "& .MuiFormControlLabel-label": { fontFamily: CMP.font },
        },
        radioText: { display: "flex", flexDirection: "column", gap: "4px" },
        radioTitle: { fontSize: "14px", fontWeight: 600, color: CMP.text },
        radioDesc: { fontSize: "13px", color: CMP.text },
        toggle: {
            width: "44px",
            height: "24px",
            borderRadius: "999px",
            display: "inline-flex",
            alignItems: "center",
            padding: "2px",
            cursor: "pointer",
            borderStyle: "solid",
            borderWidth: "1.5px",
            alignSelf: "flex-start",
        },
        toggleKnob: { width: "17px", height: "17px", borderRadius: "50%" },
        daysBox: {
            border: `1.5px dotted ${CMP.gray600}`,
            borderRadius: "14px",
            padding: "18px 26px",
            display: "flex",
            gap: "32px",
            flexWrap: "wrap" as const,
            marginTop: "8px",
            [mobile]: { gap: "18px", padding: "16px" },
        },
        dayOption: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
        },
        dayBox: {
            width: "22px",
            height: "22px",
            borderRadius: "6px",
            background: CMP.white,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
        },
        dayName: { fontSize: "14px", fontWeight: 600, color: CMP.text },
        dateGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "40px",
            marginTop: "10px",
            maxWidth: "680px",
            [mobile]: { gridTemplateColumns: "1fr", gap: "20px" },
        },
        dateColumn: { display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", minWidth: 0 },
        dateLabel: { fontSize: "14px", fontWeight: 600, color: CMP.text },
        darkNote: {
            background: CMP.black900,
            color: CMP.white,
            borderRadius: "14px",
            padding: "18px 24px",
            marginTop: "18px",
            width: "100%",
            fontSize: "14px",
            lineHeight: 1.7,
        },
        redeemWrap: { marginTop: "12px", maxWidth: "560px", width: "100%" },
        redeemPill: {
            display: "inline-flex",
            background: CMP.gold500,
            color: CMP.white,
            borderRadius: "999px",
            padding: "10px 24px",
            fontSize: "14px",
            position: "relative" as const,
            zIndex: 1,
            marginLeft: "20px",
            whiteSpace: "pre" as const,
        },
        redeemBox: {
            border: `1px solid ${CMP.gray400}`,
            borderRadius: "14px",
            padding: "34px 28px 28px",
            marginTop: "-19px",
            display: "flex",
            flexDirection: "column",
            gap: "22px",
            background: CMP.white,
            [mobile]: { padding: "32px 16px 20px" },
        },
        redeemInput: {
            fontSize: "14px",
            border: `1px solid ${CMP.gray400}`,
            borderRadius: "8px",
            padding: "12px 14px",
            outline: "none",
            color: CMP.text,
            width: "100%",
            background: CMP.white,
        },
        redeemInputError: { borderColor: `${CMP.red} !important` },
        employeeBox: {
            position: "relative" as const,
            border: `1.5px solid ${CMP.gold500}`,
            borderRadius: "12px",
            padding: "36px 28px 28px",
            marginTop: "12px",
            [mobile]: { padding: "32px 16px 20px" },
        },
        employeePill: {
            position: "absolute" as const,
            top: "-15px",
            left: "16px",
            background: CMP.gold500,
            color: CMP.white,
            fontSize: "12px",
            fontWeight: 600,
            padding: "6px 18px",
            borderRadius: "999px",
        },
        howToTitle: { fontSize: "18px", fontWeight: 600, color: CMP.gold600, marginBottom: "14px" },
        optionHeading: { fontSize: "14px", fontWeight: 600, color: CMP.text, marginBottom: "6px" },
        optionText: { fontSize: "14px", color: CMP.text, lineHeight: 1.7, margin: "0 0 8px" },
        csvButtons: { display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-start" },
        importCsvButton: {
            display: "inline-flex",
            background: CMP.gold500,
            color: CMP.white,
            border: "none",
            borderRadius: "8px",
            padding: "10px 22px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { background: CMP.gold600 },
        },
        sampleButton: {
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: CMP.white,
            color: CMP.gold600,
            border: `1.5px solid ${CMP.gold500}`,
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
        },
        emailSection: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "20px" },
        emailBox: {
            border: `1px solid ${CMP.gray400}`,
            borderRadius: "8px",
            padding: "8px",
            width: "100%",
            minHeight: "52px",
            maxHeight: "200px",
            overflowY: "auto" as const,
            "& .MuiAutocomplete-root": { width: "100%" },
            "& .MuiOutlinedInput-root": {
                padding: "0 !important",
                display: "flex",
                flexWrap: "wrap" as const,
                alignItems: "center",
                gap: "8px",
                "& fieldset": { border: "none" },
            },
            "& .MuiAutocomplete-endAdornment": { display: "none" },
            "& .MuiInputBase-input": {
                fontFamily: CMP.font,
                fontSize: "14px",
                color: CMP.text,
                flex: "1 1 200px",
                minWidth: "200px !important",
                padding: "6px !important",
            },
        },
        emailChip: {
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            border: `1px solid ${CMP.gray400}`,
            borderRadius: "999px",
            padding: "5px 12px",
            fontSize: "13px",
            color: CMP.text,
            maxWidth: "100%",
            background: CMP.white,
        },
        emailChipText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const },
        emailChipRemove: {
            display: "inline-flex",
            width: "15px",
            height: "15px",
            borderRadius: "50%",
            background: CMP.red,
            color: CMP.white,
            fontSize: "10px",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "none",
            padding: 0,
            flexShrink: 0,
        },
        importantNote: {
            background: CMP.black900,
            color: CMP.white,
            borderRadius: "6px",
            padding: "16px 20px",
            marginTop: "16px",
            maxWidth: "560px",
        },
        importantNoteTitle: { fontSize: "13px", fontWeight: 600, letterSpacing: "0.02em", lineHeight: 1.6 },
        importantNoteText: { fontSize: "13px", marginTop: "8px" },
        submitRow: { display: "flex", justifyContent: "center", marginTop: "32px", padding: "0 16px" },
        submitButton: {
            background: CMP.gold500,
            color: CMP.white,
            border: "none",
            borderRadius: "8px",
            padding: "13px 64px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            minWidth: "200px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { background: CMP.gold600 },
            "&:disabled": { opacity: 0.7, cursor: "not-allowed" },
        },
        // Banner section
        bannerSection: { display: "flex", flexDirection: "column", gap: "14px" },
        bannerPill: {
            alignSelf: "flex-start",
            color: CMP.white,
            fontSize: "14px",
            fontWeight: 600,
            padding: "9px 20px",
            borderRadius: "999px",
        },
        bannerCenterText: { fontSize: "14px", color: CMP.text, lineHeight: 1.6, textAlign: "center" as const, margin: 0 },
        clickHere: {
            color: CMP.gold500,
            fontWeight: 600,
            textDecoration: "underline",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
            fontSize: "inherit",
        },
        bannerGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "24px",
            alignItems: "stretch",
            margin: "8px auto 0",
            width: "100%",
            [mobile]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" },
        },
        bannerTile: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            minWidth: 0,
            width: "100%",
        },
        bannerTileLabel: { fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" as const },
        bannerLocked: {
            width: "100%",
            background: CMP.gray400,
            borderRadius: "10px",
            aspectRatio: "16 / 9",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            minWidth: 0,
            padding: "8px",
        },
        bannerUpload: {
            width: "100%",
            flex: 1,
            border: `2px solid ${CMP.gold500}`,
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            cursor: "pointer",
            minWidth: 0,
            padding: "8px",
            minHeight: "120px",
            background: CMP.white,
        },
        bannerUploadText: { fontSize: "14px", fontWeight: 600, textAlign: "center" as const },
        bannerFilled: {
            width: "100%",
            flex: 1,
            border: `2px solid ${CMP.gold500}`,
            borderRadius: "12px",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
        },
        bannerMedia: { width: "100%", aspectRatio: "16 / 9", position: "relative" as const },
        bannerImage: {
            position: "absolute" as const,
            inset: 0,
            width: "100%",
            height: "100%",
            borderRadius: "8px",
            objectFit: "cover" as const,
        },
        bannerVideo: {
            position: "absolute" as const,
            inset: 0,
            borderRadius: "8px",
            border: `1px solid ${CMP.gold500}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            color: CMP.gold500,
            fontSize: "13px",
            fontWeight: 600,
        },
        bannerControls: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap" as const,
            gap: "6px",
            minWidth: 0,
        },
        bannerControlGroup: { display: "flex", gap: "6px" },
        bannerEdit: {
            border: `1px solid ${CMP.gray400}`,
            background: CMP.white,
            borderRadius: "6px",
            padding: "5px 8px",
            cursor: "pointer",
            display: "inline-flex",
        },
        bannerDelete: {
            border: `1px solid ${CMP.red}`,
            background: CMP.red,
            borderRadius: "6px",
            padding: "5px 8px",
            cursor: "pointer",
            display: "inline-flex",
        },
        bannerArrow: {
            display: "inline-flex",
            width: "26px",
            height: "24px",
            borderRadius: "6px",
            alignItems: "center",
            justifyContent: "center",
            color: CMP.white,
            fontSize: "12px",
            fontWeight: 600,
            border: "none",
            padding: 0,
        },
        bannerFooter: { fontSize: "14px", color: CMP.text, textAlign: "center" as const, marginTop: "12px" },
        previewRow: { display: "flex", justifyContent: "center" },
        previewButton: {
            color: CMP.white,
            border: "none",
            borderRadius: "8px",
            padding: "12px 28px",
            fontSize: "15px",
            fontWeight: 600,
        },
    })
);
